"use client";

import { ImageWithFallback } from "./figma/ImageWithFallback";
import {
  Car,
  Plane,
  Train,
  Clock,
  DollarSign,
  MapPin,
  CheckCircle,
  ArrowRight,
  Calendar,
  Star,
  Compass,
  Check,
  AlertTriangle,
  Info,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { fetchGuideBySlug } from "@/services/travelGuide.services";
import { TravelGuideInfo } from "@/types/travelGuide.types";
import dynamic from "next/dynamic";
const ImageGalleryModal = dynamic(() => import("./ImageGalleryModal"), {
  ssr: false,
});
import { useAppSelector } from "@/hooks/hooks";

interface TravelGuidePageProps {
  guideSlug: string;
  initialGuide?: TravelGuideInfo | null;
}

export default function TravelGuidePage({
  guideSlug,
  initialGuide = null,
}: TravelGuidePageProps) {
  const [guide, setGuide] = useState<TravelGuideInfo | null>(initialGuide);
  const [loading, setLoading] = useState<boolean>(!initialGuide);
  const [error, setError] = useState<string | null>(null);
  const { HeaderMenuList } = useAppSelector((state) => state.home);

  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    // Skip fetch when server pre-fetched data is already available
    if (initialGuide && initialGuide.AttractionURL === guideSlug) return;

    let isMounted = true;
    async function loadGuideData() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchGuideBySlug(guideSlug);
        if (isMounted) {
          setGuide(data);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(
            err?.message ||
              "Something went wrong while fetching Travel Guide details.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadGuideData();
    return () => {
      isMounted = false;
    };
  }, [guideSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-20 px-4">
        <div className="relative flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-pink-600"></div>
          <Compass className="absolute w-6 h-6 text-pink-600 animate-pulse" />
        </div>
        <p className="mt-6 text-gray-600 text-lg font-medium animate-pulse">
          Loading travel guide details...
        </p>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-20 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-gray-150 shadow-xl text-center">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 mx-auto mb-6">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error ? "Failed to Load Guide" : "Travel Guide Not Found"}
          </h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {error ||
              "We couldn't find the travel guide you're looking for. Please check the URL or try searching again."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/"
              className="px-6 py-3 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white rounded-full font-semibold hover:shadow-lg transition cursor-pointer"
            >
              Back to Home
            </Link>
            {error && (
              <button
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  fetchGuideBySlug(guideSlug).then((data) => {
                    setGuide(data);
                    setLoading(false);
                  });
                }}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full font-semibold transition"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Map fields safely supporting legacy mock data and live attraction API data
  const title = guide.Title || guide.title || "Travel Guide";
  const subtitle = guide.TagLine || guide.subtitle || "";
  const distance = guide.Distance || guide.distance || "";
  const travelTime = guide.TravelTime || "";
  const duration = guide.Duration || "";
  const category = guide.AttractionCategory || "";
  const isMustVisit = guide.IsMustVisit || false;
  const resortName = guide.ResortName || "";

  // Banner image resolution
  const heroImage =
    guide.MainImage || guide.BannerImage || guide.image || "/placeholder.png";

  // Gallery items extraction
  const galleryImages = guide.Images?.map((img: any) => img.Image) || [];
  if (
    galleryImages.length === 0 &&
    (guide.MainImage || guide.BannerImage || guide.image)
  ) {
    if (guide.MainImage) galleryImages.push(guide.MainImage);
    if (guide.BannerImage) galleryImages.push(guide.BannerImage);
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ─── HERO COVER BANNER ─── */}
      <div className="relative h-[480px] w-full overflow-hidden">
        <ImageWithFallback
          src={heroImage}
          alt={title}
          className="w-full h-full object-cover transform scale-105 hover:scale-100 transition-transform duration-[8000ms]"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent"></div>
        <div className="absolute inset-x-0 bottom-0 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {category && (
                  <span className="bg-pink-600/90 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                    {category}
                  </span>
                )}
                {isMustVisit && (
                  <span className="bg-yellow-400 text-gray-950 text-xs font-bold px-3.5 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-sm">
                    <Star className="w-3.5 h-3.5 fill-current" /> Must Visit
                  </span>
                )}
                {resortName && (
                  <span className="bg-white/10 backdrop-blur-md text-white text-xs font-medium px-3.5 py-1.5 rounded-full border border-white/20">
                    Near {resortName}
                  </span>
                )}
              </div>

              {/* Title & Tagline */}
              <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight mb-3">
                {title}
              </h1>
              {subtitle && (
                <p className="text-xl sm:text-2xl text-gray-200 font-normal leading-relaxed mb-6">
                  {subtitle}
                </p>
              )}

              {/* Quick Info Grid */}
              <div className="flex flex-wrap gap-4 sm:gap-6 text-sm text-white">
                {distance && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10">
                    <MapPin className="w-4 h-4 text-pink-400" />
                    <span className="font-medium">Distance: {distance}</span>
                  </div>
                )}
                {travelTime && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10">
                    <Clock className="w-4 h-4 text-pink-400" />
                    <span className="font-medium">
                      Travel Time: {travelTime}
                    </span>
                  </div>
                )}
                {duration && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10">
                    <Calendar className="w-4 h-4 text-pink-400" />
                    <span className="font-medium">
                      Stay Duration: {duration}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── PAGE BODY GRID ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12 items-start">
          {/* LEFT 2/3 COLUMN: Rich Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview / Description */}
            <div className="bg-white rounded-3xl border border-gray-150 p-8 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-3 border-b border-gray-100">
                Overview & Description
              </h2>
              {guide.Description ? (
                <div
                  className="text-gray-700 text-base leading-relaxed blog-content select-text"
                  dangerouslySetInnerHTML={{ __html: guide.Description }}
                />
              ) : (
                <p className="text-gray-700 text-base leading-relaxed select-text">
                  {guide.overview}
                </p>
              )}
            </div>

            {/* Why Visit */}
            {guide.WhyVisit && guide.WhyVisit.length > 0 && (
              <div className="bg-gradient-to-br from-pink-50/30 to-purple-50/30 rounded-3xl border border-pink-100/60 p-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-pink-500 fill-current" />
                  Why You Should Visit
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {guide.WhyVisit.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl p-5 border border-pink-100/50 shadow-sm hover:shadow-md transition duration-300"
                    >
                      <h4 className="text-lg font-bold text-gray-950 mb-2">
                        {item.Title}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.Description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Things To Do */}
            {guide.ThingsToDo && guide.ThingsToDo.length > 0 && (
              <div className="bg-white rounded-3xl border border-gray-150 p-8 shadow-sm">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-3 border-b border-gray-100">
                  <Compass className="w-6 h-6 text-pink-500" />
                  Key Things to Do
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {guide.ThingsToDo.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 bg-gray-50/60 rounded-xl p-4 border border-gray-100"
                    >
                      <div className="w-6 h-6 bg-pink-50 rounded-full flex items-center justify-center text-pink-600 flex-shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <span className="text-gray-700 text-sm font-medium">
                        {item.Description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dos & Don'ts */}
            {(guide.Dos || guide.Donts) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {guide.Dos && (
                  <div className="bg-green-50/40 border border-green-200/60 rounded-3xl p-8 shadow-sm">
                    <h3 className="text-xl font-semibold text-green-800 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Do's & Guidelines
                    </h3>
                    <div
                      className="text-gray-700 text-sm leading-relaxed blog-content select-text font-normal"
                      dangerouslySetInnerHTML={{ __html: guide.Dos }}
                    />
                  </div>
                )}
                {guide.Donts && (
                  <div className="bg-red-50/40 border border-red-200/60 rounded-3xl p-8 shadow-sm">
                    <h3 className="text-xl font-semibold text-red-800 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Don'ts & Restrictions
                    </h3>
                    <div
                      className="text-gray-700 text-sm leading-relaxed blog-content select-text font-normal"
                      dangerouslySetInnerHTML={{ __html: guide.Donts }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* How to Reach (API Data) */}
            {guide.HowToReach && guide.HowToReach.length > 0 && (
              <div className="bg-white rounded-3xl border border-gray-150 p-8 shadow-sm">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-3 border-b border-gray-100">
                  How to Reach
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {guide.HowToReach.map((item, index) => {
                    const iconStyle =
                      "w-10 h-10 rounded-2xl flex items-center justify-center mb-4";
                    let icon = <Car className="w-5 h-5" />;
                    let containerClass = "bg-yellow-50 text-yellow-600";

                    if (
                      item.Title.toLowerCase().includes("train") ||
                      item.Title.toLowerCase().includes("rail")
                    ) {
                      icon = <Train className="w-5 h-5" />;
                      containerClass = "bg-green-50 text-green-600";
                    } else if (
                      item.Title.toLowerCase().includes("air") ||
                      item.Title.toLowerCase().includes("flight")
                    ) {
                      icon = <Plane className="w-5 h-5" />;
                      containerClass = "bg-blue-50 text-blue-600";
                    }

                    return (
                      <div
                        key={index}
                        className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 flex flex-col justify-between"
                      >
                        <div>
                          <div className={`${iconStyle} ${containerClass}`}>
                            {icon}
                          </div>
                          <h4 className="text-lg font-bold text-gray-950 mb-2">
                            {item.Title}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {item.Description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legacy Travel Guides Block (Road, flight, train) */}
            {guide.roadTravel && (
              <div className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-2xl flex items-center justify-center">
                    <Car className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl">By Road</h2>
                    <p className="text-gray-600">Most Popular & Scenic Route</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Clock className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Duration</p>
                      <p className="text-xl">{guide.roadTravel.duration}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <MapPin className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Distance</p>
                      <p className="text-xl">{guide.roadTravel.distance}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <DollarSign className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Cost Range</p>
                      <p className="text-xl">{guide.roadTravel.cost}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {guide.roadTravel.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {guide.roadTravel.options.map(
                    (option: any, index: number) => (
                      <div
                        key={index}
                        className={`bg-white rounded-2xl border-2 p-6 ${
                          option.recommended
                            ? "border-pink-500 shadow-lg"
                            : "border-gray-200"
                        }`}
                      >
                        {option.recommended && (
                          <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white text-xs px-3 py-1 rounded-full mb-4 inline-block">
                            Recommended
                          </span>
                        )}
                        <h3 className="text-xl mb-4">{option.type}</h3>
                        <div className="mb-4">
                          <p className="text-2xl text-pink-600 mb-1">
                            {option.cost}
                          </p>
                          <p className="text-sm text-gray-600">
                            {option.duration}
                          </p>
                        </div>
                        <ul className="space-y-2">
                          {option.pros.map((pro: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-gray-700"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
                  <h3 className="text-2xl mb-6">Suggested Route</h3>
                  <div className="space-y-4">
                    {guide.roadTravel.route.map((step: any, index: any) => (
                      <div key={index} className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1 bg-white rounded-lg p-4">
                          <p className="text-gray-700">{step}</p>
                        </div>
                        {index < (guide.roadTravel?.route.length ?? 0) - 1 && (
                          <ArrowRight className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {guide.flightTravel && (
              <div className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
                    <Plane className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl">By Flight</h2>
                    <p className="text-gray-600">Fastest Way to Reach</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Duration</p>
                      <p className="text-xl">
                        {guide.flightTravel.totalDuration}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Cost</p>
                      <p className="text-xl">{guide.flightTravel.totalCost}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {guide.flightTravel.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {guide.flightTravel.details.map((detail: any, index: any) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200"
                    >
                      <h3 className="text-xl mb-4 text-gray-900">
                        {detail.title}
                      </h3>
                      <ul className="space-y-2">
                        {detail.items.map((item: string, i: number) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-gray-700"
                          >
                            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {guide.trainTravel && (
              <div className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-400 via-teal-500 to-blue-600 rounded-2xl flex items-center justify-center">
                    <Train className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl">By Train</h2>
                    <p className="text-gray-600">Comfortable & Economical</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 mb-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {guide.trainTravel.description}
                  </p>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl mb-6">Available Trains</h3>
                  <div className="space-y-4">
                    {guide.trainTravel.trains.map((train: any, index: any) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-pink-300 hover:shadow-lg transition"
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h4 className="text-xl mb-1">{train.name}</h4>
                            <p className="text-gray-600">
                              Train No: {train.number}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Departure</p>
                              <p className="text-gray-900">{train.departure}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Arrival</p>
                              <p className="text-gray-900">{train.arrival}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Duration</p>
                              <p className="text-gray-900">{train.duration}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Frequency</p>
                              <p className="text-gray-900">{train.days}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl mb-6">Ticket Prices by Class</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {guide.trainTravel.classes.map((cls: any, index: any) => (
                      <div
                        key={index}
                        className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 text-center"
                      >
                        <p className="text-2xl mb-2">{cls.type}</p>
                        <p className="text-xl text-green-700">{cls.cost}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                  <p className="text-gray-700">
                    <strong>From Bhuj Railway Station:</strong>{" "}
                    {guide.trainTravel.fromStation}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT 1/3 COLUMN: Quick Cards & Sticky Sidebar */}
          <div className="space-y-8 mt-12 lg:mt-0">
            {/* Best Time to Visit Card */}
            {(guide.BestTime ||
              (guide.BestTimes && guide.BestTimes.length > 0)) && (
              <div className="bg-white rounded-3xl border border-gray-150 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-950 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-pink-500" />
                  Best Time to Visit
                </h3>
                {guide.BestTime && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 font-medium">
                      Recommended Months
                    </p>
                    <p className="text-base font-semibold text-gray-800">
                      {guide.BestTime}
                    </p>
                  </div>
                )}
                {guide.BestTimes && guide.BestTimes.length > 0 && (
                  <div className="space-y-4">
                    {guide.BestTimes.map((item, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-2xl p-4 border border-gray-100"
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-bold text-gray-900">
                            {item.Title}
                          </span>
                          <span className="text-xs bg-pink-100 text-pink-700 font-semibold px-2 py-0.5 rounded-full">
                            {item.SeasonType}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 leading-normal">
                          {item.Description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Nearby / Map Guide */}
            {guide.Nearby && guide.Nearby.length > 0 && (
              <div className="bg-white rounded-3xl border border-gray-150 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-950 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-pink-500" />
                  Nearby Attractions
                </h3>
                <div className="space-y-4">
                  {guide.Nearby.map((item, index) => (
                    <Link
                      key={index}
                      href={`/travel-guide/${item.AttractionURL}`}
                      className="group flex gap-3 items-center p-3 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-md transition duration-300 bg-white"
                    >
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 relative flex-shrink-0">
                        <ImageWithFallback
                          src={item.MainImage}
                          alt={item.Title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-grow">
                        <h4 className="text-sm font-bold text-gray-900 group-hover:text-pink-600 transition-colors line-clamp-1">
                          {item.Title}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                          <Compass className="w-3.5 h-3.5" />
                          <span>
                            {item.KmAwayFromMainAttraction.toFixed(1)} km away
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-pink-600 group-hover:translate-x-0.5 transition duration-300" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Safety Guidelines Widget */}
            {/* <div className="bg-yellow-50/40 border border-yellow-200/60 rounded-3xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-yellow-800 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Quick Info Tip
              </h3>
              <p className="text-xs text-gray-700 leading-relaxed font-normal">
                Permits are mandatory for entering the White Rann salt desert.
                Ensure you carry physical copies of government photo identity
                proofs (Adhaar Card/Passport) to ease checkpoint approvals.
              </p>
            </div> */}
          </div>
        </div>

        {/* ─── PHOTO GALLERY SECTION ─── */}
        {galleryImages.length > 0 && (
          <section className="mt-16 border-t border-gray-100 pt-16">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl font-semibold text-gray-950 mb-2">
                Visual Captures & Gallery
              </h2>
              <p className="text-gray-600 font-normal text-sm">
                A visual journey through the breathtaking terrains and
                highlights of Kutch.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedImageIndex(index);
                    setGalleryOpen(true);
                  }}
                  className="group relative h-48 rounded-2xl overflow-hidden cursor-pointer bg-gray-100 border border-gray-200/60 hover:shadow-xl hover:border-pink-200 transition duration-300"
                >
                  <ImageWithFallback
                    src={image}
                    alt={`${title} Gallery ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="text-xs bg-white/90 text-gray-900 font-bold px-3 py-1.5 rounded-full shadow-md uppercase tracking-wider scale-95 group-hover:scale-100 transition duration-300">
                      View Photo
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── PACKAGES CTA FOOTER BANNER ─── */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-600 to-pink-600 rounded-3xl p-6 md:p-12 text-center text-white mt-20 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-12 translate-x-12 blur-2xl"></div>
          <div className="relative max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Explore Premium Rann Utsav Packages
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-white/90 font-light leading-relaxed">
              Book our all-inclusive tour packages including comfortable
              accommodations, gourmet local meals, and direct transfers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {HeaderMenuList?.PackageList?.slice(0, 3).map(
                (item: any, index: number) => (
                  <Link
                    href={`/package/${item.URL}`}
                    className="bg-white text-pink-600 font-semibold px-8 py-4 rounded-full hover:shadow-2xl hover:scale-105 active:scale-100 transition cursor-pointer"
                    key={index}
                  >
                    {item.Title}
                  </Link>
                ),
              )}
              {/* <Link
                href="/package/2n-3d"
                className="bg-white text-pink-600 font-semibold px-8 py-4 rounded-full hover:shadow-2xl hover:scale-105 active:scale-100 transition cursor-pointer"
              >
                2 Nights / 3 Days Package
              </Link>
              <Link
                href="/package/3n-4d"
                className="bg-white text-pink-600 font-semibold px-8 py-4 rounded-full hover:shadow-2xl hover:scale-105 active:scale-100 transition cursor-pointer"
              >
                3 Nights / 4 Days Package
              </Link> */}
            </div>
          </div>
        </div>
      </div>

      {/* GALLERY LIGHTBOX MODAL */}
      <ImageGalleryModal
        images={galleryImages}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
}
